import {
  AccountInfo,
  AuthenticationResult,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import {
  useMount,
  useMouse,
  usePrevious,
  useSessionStorageState,
} from "ahooks";
import * as React from "react";

import { AuthenticateApi, LoginDto, UserAction, UserProfileDto } from "../api";
import { useCommonHooks, useLanguages, useRouter } from "../hooks";
import { baseUrl } from "../hooks";
import { GetUserLoginNameFromSpGlobgalContext } from "../utils/sharepointHelperFunctions";
import { useFrontendSettings } from "../hooks/useFrontendSettings";
import { useWhatChanged } from "@simbathesailor/use-what-changed";

interface IAuthProviderProps {
  autoLoginAzureAd: boolean;
}

export interface ErrorDetails {
  statusCode?: number;
  message?: string | null;
}

const AuthContext = React.createContext<IAuthContext>(null);

export const AuthProvider: React.FunctionComponent<IAuthProviderProps> = ({
  autoLoginAzureAd,
  children,
}) => {
  const [logoutReason, setLogoutReason] = React.useState<string>();
  const [user, setUser] = React.useState<UserProfileDto>();
  const isLoggedIn = React.useMemo(() => !!user, [user]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [loginError, setLoginError] = React.useState();
  const [azureAdAccount, setAzureAdAccount] = React.useState<AccountInfo>();
  const [, setRedirectUri] = useSessionStorageState<string>(
    "redirect_uri",
    null
  );
  const [lastAccountName, setLastAccountName] = useSessionStorageState<string>(
    "account_name",
    null
  );
  const finalLastAccountName = React.useMemo(
    () => GetUserLoginNameFromSpGlobgalContext() || lastAccountName,
    [lastAccountName]
  );
  const { location, push } = useRouter();

  const { frontendSettings } = useFrontendSettings();

  const msalInstance = React.useMemo(() => {
    if (frontendSettings) {
      return new PublicClientApplication({
        auth: {
          clientId: frontendSettings.azureClientId,
          authority: frontendSettings.azureAuthority,
        },
        cache: {
          cacheLocation: "localStorage", // This configures where your cache will be stored
          storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
        },
        system: {
          loggerOptions: {
            loggerCallback: (logLevel, message) => {
              // eslint-disable-next-line no-console
              console.log("[MSAL]", message);
            },
            piiLoggingEnabled: true,
            logLevel: LogLevel.Warning,
          },
        },
      });
    }
  }, [frontendSettings]);

  const getAccessToken = React.useCallback(async () => {
    if (!azureAdAccount && user) return null;

    let refreshedToken: AuthenticationResult;
    try {
      if (azureAdAccount) {
        refreshedToken = await msalInstance.acquireTokenSilent({
          account: azureAdAccount,
          scopes: [frontendSettings?.azureApiScope],
        });
        return "Bearer " + refreshedToken.accessToken;
      }
    } catch (error) {
      console.error("Unable to get token silently", error);
      await msalInstance.loginRedirect({
        scopes: [frontendSettings?.azureApiScope],
        loginHint: finalLastAccountName,
      });
    }

    return null;
  }, [
    azureAdAccount,
    finalLastAccountName,
    frontendSettings?.azureApiScope,
    msalInstance,
    user,
  ]);

  const authApiGetter = React.useCallback(async () => {
    return new AuthenticateApi({
      basePath: baseUrl,
      apiKey: await getAccessToken(),
      middlewares: [
        {
          pre(context: RequestInit): Promise<RequestInit | void> {
            context.credentials = "include";
            return Promise.resolve(context);
          },
          post(response: Response): Promise<Response | void> {
            return Promise.resolve(response);
          },
        },
      ],
    });
  }, [getAccessToken]);

  const logout = React.useCallback(
    async (reason?: string) => {
      setIsLoading(true);
      setLogoutReason(reason);
      setUser(null);
      setAzureAdAccount(null);
      const authApi = await authApiGetter();
      await authApi.logout();
      if (azureAdAccount) {
        await msalInstance.logoutRedirect();
      }
      setIsLoading(false);
    },
    [authApiGetter, azureAdAccount, msalInstance]
  );

  const loginAzureAd = React.useCallback(async () => {
    setIsLoading(true);
    await msalInstance.loginRedirect({
      scopes: [frontendSettings?.azureApiScope],
      loginHint: finalLastAccountName,
    });
  }, [finalLastAccountName, frontendSettings?.azureApiScope, msalInstance]);

  const loginProcess = React.useCallback(async () => {
    try {
      console.log("login...");
      // Handle redirectPromise
      const authApi = await authApiGetter();
      setIsLoading(true);
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse) {
        await authApi.logout();
        setAzureAdAccount(redirectResponse.account);
        setLastAccountName(redirectResponse.account.username);
      } else {
        const account = msalInstance.getAccountByUsername(finalLastAccountName);
        if (account) {
          console.log("Local account found ...");
          setAzureAdAccount(account);
        } else {
          if (autoLoginAzureAd) {
            await loginAzureAd();
          } else {
            const profile = await authApi.profile();
            setUser(profile);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    authApiGetter,
    autoLoginAzureAd,
    finalLastAccountName,
    loginAzureAd,
    msalInstance,
    setLastAccountName,
  ]);

  const [loginStarted, setLoginStarted] = React.useState(false);
  React.useEffect(() => {
    if (!isLoggedIn && msalInstance && !loginStarted) {
      setLoginStarted(true);
      loginProcess();
    }
  }, [isLoggedIn, loginProcess, loginStarted, msalInstance]);

  const loadProfile = React.useCallback(async () => {
    try {
      const authApi = await authApiGetter();
      const profile = await authApi.profile();
      setUser(profile);
    } catch (error) {
      setLoginError(error);
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [authApiGetter, logout]);

  React.useEffect(() => {
    if (azureAdAccount && !user) {
      loadProfile().then(() => {
        console.log("Profile loaded !");
      });
    }
  }, [azureAdAccount, loadProfile, user]);

  const login = React.useCallback(
    async (login: string, password: string, token?: string) => {
      setIsLoading(true);
      const authApi = await authApiGetter();
      try {
        const profile = await authApi.login(
          new LoginDto({
            login,
            password,
            token,
          })
        );
        setUser(profile);
        setLoginError(null);
      } catch (error) {
        setLoginError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [authApiGetter]
  );

  const [expirationDate, setexpirationDate] = React.useState<Date>(new Date());
  const { pageX, pageY } = useMouse();
  const previousMousePosition = usePrevious({ pageX, pageY });
  React.useEffect(() => {
    if (
      !!previousMousePosition &&
      !!previousMousePosition.pageX &&
      !!previousMousePosition.pageY &&
      (previousMousePosition?.pageX !== pageX ||
        previousMousePosition?.pageY !== pageY)
    ) {
      // check if soon expired or expired
      let date = new Date();
      date.setMinutes(date.getMinutes() + 15);
      setexpirationDate(date);
    }
  }, [pageX, pageY, previousMousePosition]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      if (!!expirationDate && now > expirationDate) {
        logout("SessionExpired");
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [expirationDate, logout]);

  const hasAction = React.useCallback(
    (actionList: UserAction[] = []) => {
      return actionList.filter((v) => user?.actions.includes(v)).length > 0;
    },
    [user?.actions]
  );

  const fullUserName = React.useMemo(() => {
    return user?.login;
  }, [user?.login]);

  return (
    <AuthContext.Provider
      value={{
        authError: loginError,
        hasAction,
        authLoading: isLoading,
        logout,
        logoutReason,
        user,
        isLoggedIn,
        login,
        loginAzureAd,
        fullUserName,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface IAuthContext {
  logout: () => void;
  authLoading: boolean;
  user?: UserProfileDto;
  authError?: ErrorDetails;
  hasAction: (actionList: UserAction[]) => boolean;
  logoutReason: string;
  isLoggedIn: boolean;
  login: (user: string, password: string, token?: string) => Promise<any>;
  loginAzureAd: () => Promise<any>;
  fullUserName: string;
  getAccessToken: () => Promise<string> | undefined;
}

export const useAuth = () => React.useContext(AuthContext);
